/**
 * Pure helpers for channel resolution. Kept separate from the React
 * hook so the AssistantShell voice-only fast path can sanitize
 * channels WITHOUT mounting any hook — preserving byte-for-byte
 * unchanged voice behavior for current production bots (Codex B1).
 */

import { WIDGET_CHANNELS, type WidgetChannel } from '../types';

const WIDGET_CHANNEL_SET = new Set<string>(WIDGET_CHANNELS);

export function isWidgetChannel(value: unknown): value is WidgetChannel {
  return typeof value === 'string' && WIDGET_CHANNEL_SET.has(value);
}

/**
 * Sanitize whatever the server hands us into a non-empty, deduped list
 * of WIDGET-only channels. Always falls back to ['voice'].
 *
 * Used by:
 *   - `AssistantShell` voice-only fast path (synchronous, no hook).
 *   - `useAssistantChannels` initial resolution.
 */
export function sanitizeAvailableChannels(
  enabledChannels: WidgetChannel[] | string[] | null | undefined
): WidgetChannel[] {
  const raw = Array.isArray(enabledChannels) ? enabledChannels : [];
  const filtered = raw.filter(isWidgetChannel);
  const deduped = Array.from(new Set(filtered));
  return deduped.length > 0 ? deduped : ['voice'];
}

export function isVoiceOnly(
  enabledChannels: WidgetChannel[] | string[] | null | undefined
): boolean {
  const a = sanitizeAvailableChannels(enabledChannels);
  return a.length === 1 && a[0] === 'voice';
}

/**
 * Pure resolver for "is there already a valid pick the user committed to?"
 * Used by both the hook's lazy initial state AND the "channels grew" branch
 * to decide whether the chooser must be shown.
 *
 * A valid pick exists when EITHER:
 *  - the user previously chose a channel (remembered, only if remembering is on), OR
 *  - the admin set a `defaultChannel` that is still in availableChannels.
 *
 * (Codex round-3 blocker fix — defaultChannel must also suppress the chooser.)
 */
export function resolveValidPick(opts: {
  remembered: WidgetChannel | null | undefined;
  rememberEnabled: boolean;
  defaultChannel: WidgetChannel | null | undefined;
  availableChannels: WidgetChannel[];
}): WidgetChannel | null {
  const { remembered, rememberEnabled, defaultChannel, availableChannels } = opts;
  if (rememberEnabled && remembered && availableChannels.includes(remembered)) {
    return remembered;
  }
  if (defaultChannel && availableChannels.includes(defaultChannel)) {
    return defaultChannel;
  }
  return null;
}

/**
 * Pure resolver for chooser visibility — used by the hook AND by tests.
 * Chooser is shown ONLY when the bot has multiple widget channels AND
 * no valid pick (remembered or default) is available.
 */
export function shouldShowChooser(opts: {
  availableChannels: WidgetChannel[];
  remembered: WidgetChannel | null | undefined;
  rememberEnabled: boolean;
  defaultChannel: WidgetChannel | null | undefined;
}): boolean {
  if (opts.availableChannels.length <= 1) return false;
  return resolveValidPick(opts) === null;
}

